import React from "react";
import {
  Box,
  CardHeader,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  TextField,
  Paper,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import TimelineIcon from "@mui/icons-material/Timeline";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import {
  GlassCard,
  QuestionCard,
  ProgressIndicator,
  ReferenceBadge,
} from "./evaluateAppraisal.styled";
import {
  RATING_MIN,
  RATING_MAX,
  RATING_STEP,
  isMeaningfulAnswerLike,
  isRowChanged,
} from "./evaluateAppraisal.utils";

export default function QuestionsCard({
  noSelectedStep,
  bankCount,
  stepLoading,
  questions,
  filteredQuestions,
  expandedQuestions,
  onToggleQuestion,
  registerQuestionRef,
  questionFilter,
  onChangeFilter,
  onJumpNext,
  isSelfReview,
  isEditable,
  baselineByQid,
  referenceInfo,
  fieldErrors,
  requirements,
  onJumpToRequired,
  onChangeQuestionField,
  onFocusQuestion,
  submitting,
  submitDisabled,
  onBack,
  onSubmit,
  overallAutoValue,
}) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Design constants
  const BORDER_RADIUS = "8px";
  const QUESTION_RADIUS = "8px";
  const INPUT_RADIUS = "8px";
  const SOFT_SHADOW = theme.hr?.shadow?.soft || "none";
  const LIGHT_SHADOW = theme.shadows[1];
  const SPACING_UNIT = isCompact ? 1.5 : 2;
  const CARD_PADDING = isCompact ? 1.75 : 2.25;

  const secondaryMain = theme.palette.secondary?.main || theme.palette.primary.dark;

  const headerSurface = `linear-gradient(150deg,
    ${alpha(theme.palette.primary.main, 0.1)} 0%,
    ${alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.97 : 0.88)} 48%,
    ${alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.97 : 0.88)} 100%)`;

  // Soft, theme-aware input styling shared by every editable field
  const fieldSx = (active = true) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: INPUT_RADIUS,
      backgroundColor: active
        ? alpha(theme.palette.background.paper, 0.78)
        : alpha(theme.palette.action.disabledBackground, 0.5),
      transition: "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(theme.palette.divider, 0.22),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(theme.palette.primary.main, 0.24),
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(theme.palette.primary.main, 0.34),
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(theme.palette.error.main, 0.34),
      },
    },
    "& .MuiInputLabel-root": { fontWeight: 700 },
  });

  // Compact icon status chip in the spirit of HeaderHero's statChip
  const statusChip = (label, color, Icon) => (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 15, color: `${color} !important` }} />}
      label={label}
      sx={{
        height: 24,
        borderRadius: "8px",
        fontWeight: 800,
        fontSize: "0.7rem",
        color,
        backgroundColor: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.22)}`,
        "& .MuiChip-label": { px: 0.8 },
      }}
    />
  );

  const totalQuestions = questions.length;
  const answeredCount = questions.filter((q) =>
    isMeaningfulAnswerLike(
      { rating: q.rating, comments: q.comments, applicability: q.applicability },
      { selfReview: isSelfReview }
    )
  ).length;
  const completionPct = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  return (
    <GlassCard
      elevation={0}
      sx={{
        borderRadius: BORDER_RADIUS,
        background: `linear-gradient(140deg, ${alpha(
          theme.palette.background.paper,
          0.94
        )} 0%, ${alpha(theme.palette.background.default, 0.78)} 100%)`,
        border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
        boxShadow:
          theme.palette.mode === "light"
            ? `0 20px 48px ${alpha(theme.palette.common.black, 0.08)}`
            : `0 22px 52px ${alpha(theme.palette.common.black, 0.34)}`,
      }}
    >
      {/* ===== Header Section (gradient hero) ===== */}
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.25} useFlexGap flexWrap="wrap">
            <Avatar
              variant="rounded"
              sx={{
                width: 38,
                height: 38,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.16)}`,
              }}
            >
              <QuestionAnswerIcon fontSize="small" />
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 850,
                letterSpacing: -0.3,
                color: theme.palette.primary.main,
                fontSize: { xs: "1.05rem", md: "1.2rem" },
              }}
            >
              Evaluation Questions
            </Typography>
            {!!questions.length && (
              <Chip
                size="small"
                label={`${filteredQuestions.length} of ${questions.length}`}
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  borderRadius: "8px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              />
            )}
          </Stack>
        }
        subheader={
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {isSelfReview
                ? "Self Review - Comments only"
                : "Rate each question and provide feedback"}
            </Typography>

            {!!totalQuestions && (
              <Box
                sx={{
                  mt: 1.25,
                  p: { xs: 1.2, sm: 1.4 },
                  borderRadius: "8px",
                  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                  background: `linear-gradient(145deg,
                    ${alpha(theme.palette.background.paper, 0.72)} 0%,
                    ${alpha(theme.palette.background.default, 0.62)} 100%)`,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.9 }}
                >
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                      {completionPct}% complete
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      {answeredCount}/{totalQuestions} answered
                    </Typography>
                  </Stack>
                  {isEditable && (requirements?.totalMissing || 0) > 0 && (
                    <Chip
                      size="small"
                      icon={<RuleRoundedIcon sx={{ fontSize: 15 }} />}
                      label={`${requirements.totalMissing} required`}
                      color="warning"
                      sx={{ height: 24, fontWeight: 800, borderRadius: "8px" }}
                    />
                  )}
                </Stack>
                <ProgressIndicator progress={completionPct} />
              </Box>
            )}
          </Box>
        }
        action={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ minWidth: isCompact ? "100%" : 320 }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 150 } }}>
              <InputLabel>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <FilterAltIcon fontSize="small" />
                  <span>Filter</span>
                </Stack>
              </InputLabel>
              <Select
                value={questionFilter}
                label="Filter"
                onChange={(e) => onChangeFilter(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: alpha(theme.palette.background.paper, 0.82),
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.divider, 0.2),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                  },
                }}
              >
                <MenuItem value="all">All questions</MenuItem>
                <MenuItem value="unanswered">Unanswered</MenuItem>
                <MenuItem value="modified">Modified</MenuItem>
                {!isSelfReview && <MenuItem value="excluded">Excluded</MenuItem>}
              </Select>
            </FormControl>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowDownwardIcon />}
              onClick={onJumpNext}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                whiteSpace: "nowrap",
                borderColor: alpha(theme.palette.primary.main, 0.24),
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                transition: "all 180ms ease",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                  backgroundColor: alpha(theme.palette.background.paper, 0.98),
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            >
              Next unanswered
            </Button>
          </Stack>
        }
        sx={{
          pb: 2.2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: headerSurface,
          "& .MuiCardHeader-content": { minWidth: 0 },
          "& .MuiCardHeader-action": {
            alignSelf: "stretch",
            width: isCompact ? "100%" : "auto",
            mt: isCompact ? 2 : 0,
            mr: isCompact ? 0 : 0.5,
          },
        }}
      />

      <CardContent sx={{ pt: 2.2, pb: { xs: 14, md: 16 } }}>
        {/* Status Alerts */}
        {isEditable && requirements.totalMissing > 0 && !stepLoading && !!questions.length && (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<RuleRoundedIcon />}
            sx={{
              mb: 3,
              borderRadius: BORDER_RADIUS,
              alignItems: "center",
              borderLeft: `4px solid ${theme.palette.warning.main}`,
              borderColor: alpha(theme.palette.warning.main, 0.22),
              background: `linear-gradient(120deg,
                ${alpha(theme.palette.warning.light, 0.16)} 0%,
                ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
            }}
            action={
              <Button
                size="small"
                variant="text"
                onClick={onJumpToRequired}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  color: theme.palette.warning.dark,
                }}
              >
                Jump to required
              </Button>
            }
          >
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Required fields missing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete {requirements.totalMissing} required item{requirements.totalMissing > 1 ? "s" : ""} before submitting.
            </Typography>
          </Alert>
        )}

        {!stepLoading && !noSelectedStep && !isEditable && (
          <Alert
            severity="info"
            variant="outlined"
            icon={<LockOutlinedIcon />}
            sx={{
              mb: 3,
              borderRadius: BORDER_RADIUS,
              alignItems: "center",
              borderLeft: `4px solid ${theme.palette.info.main}`,
              borderColor: alpha(theme.palette.info.main, 0.2),
              background: `linear-gradient(120deg,
                ${alpha(theme.palette.info.light, 0.14)} 0%,
                ${alpha(theme.palette.info.main, 0.04)} 100%)`,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              This step is view-only. Select your assigned step to make changes.
            </Typography>
          </Alert>
        )}

        {/* Empty States */}
        {noSelectedStep ? (
          <Box
            sx={{
              py: 9,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: "text.secondary",
              borderRadius: "8px",
              border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
              background: alpha(theme.palette.background.default, 0.4),
            }}
          >
            <Avatar
              variant="rounded"
              sx={{
                width: 64,
                height: 64,
                mb: 2,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: alpha(theme.palette.primary.main, 0.6),
              }}
            >
              <TimelineIcon sx={{ fontSize: 34 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={800} color="text.primary" gutterBottom>
              No step selected
            </Typography>
            <Typography variant="body2">Select a step from the timeline above to begin.</Typography>
          </Box>
        ) : bankCount === 0 ? (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              borderRadius: BORDER_RADIUS,
              borderColor: alpha(theme.palette.warning.main, 0.22),
              background: alpha(theme.palette.warning.light, 0.05),
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Question bank is empty. Add questions to begin.
            </Typography>
          </Alert>
        ) : stepLoading ? (
          <Box
            sx={{
              py: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress
              size={48}
              thickness={3}
              sx={{ color: alpha(theme.palette.primary.main, 0.7) }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 2 }}>
              Loading questions...
            </Typography>
          </Box>
        ) : !questions.length ? (
          <Alert
            severity="info"
            variant="outlined"
            icon={<InboxRoundedIcon />}
            sx={{
              borderRadius: BORDER_RADIUS,
              borderColor: alpha(theme.palette.info.main, 0.2),
              background: alpha(theme.palette.info.light, 0.05),
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              No questions generated for this step.
            </Typography>
          </Alert>
        ) : (
          <Stack spacing={SPACING_UNIT}>
            {filteredQuestions.map((q, index) => {
              const qKey = q.questionId ?? q.id ?? index;
              const prev = baselineByQid?.[Number(qKey)] ?? baselineByQid?.[String(qKey)];
              const hasPrev = !!prev?.has;
              const changed = hasPrev ? isRowChanged(q, prev, { selfReview: isSelfReview }) : false;
              const isExpanded = !!expandedQuestions[q.questionId];
              const meaningful = isMeaningfulAnswerLike(
                { rating: q.rating, comments: q.comments, applicability: q.applicability },
                { selfReview: isSelfReview }
              );
              const excluded = !isSelfReview && String(q.applicability || "YES") === "NO";
              const qErr = fieldErrors.questions?.[Number(q.questionId)] || {};
              const ratingRequired = !isSelfReview && !excluded && isEditable;

              return (
                <QuestionCard
                  key={q.questionId ?? index}
                  editable={isEditable}
                  haschanges={changed ? "true" : "false"}
                  excluded={excluded ? "true" : "false"}
                  ref={(el) => registerQuestionRef(q.questionId, el)}
                  sx={{
                    borderRadius: QUESTION_RADIUS,
                    boxShadow: SOFT_SHADOW,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: LIGHT_SHADOW,
                    },
                  }}
                >
                  <Box sx={{ p: CARD_PADDING }}>
                    {/* Question Header */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1.5}
                      sx={{ mb: 1.5 }}
                    >
                      <Stack
                        direction="row"
                        spacing={{ xs: 1, md: 1.5 }}
                        alignItems="flex-start"
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 26, md: 32 },
                            height: { xs: 26, md: 32 },
                            background: `linear-gradient(135deg,
                              ${alpha(theme.palette.primary.main, 0.18)} 0%,
                              ${alpha(secondaryMain, 0.14)} 100%)`,
                            color: theme.palette.primary.dark,
                            fontSize: { xs: 12, md: 13 },
                            fontWeight: 800,
                            flexShrink: 0,
                            mt: 0.25,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                          }}
                        >
                          {q.questionId ? String(q.questionId).slice(-2) : index + 1}
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            color="text.primary"
                            sx={{ lineHeight: 1.35 }}
                            gutterBottom
                          >
                            {q.questionTitle}
                          </Typography>

                          {q.questionDescription && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.5,
                                ...(!isExpanded && {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }),
                              }}
                            >
                              {q.questionDescription}
                            </Typography>
                          )}

                          {q.questionDescription && q.questionDescription.length > 90 && (
                            <Button
                              size="small"
                              onClick={() => onToggleQuestion(q.questionId)}
                              startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              sx={{
                                mt: 0.5,
                                px: 1,
                                textTransform: "none",
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                minHeight: 24,
                              }}
                            >
                              {isExpanded ? "Show less" : "Read more"}
                            </Button>
                          )}
                        </Box>
                      </Stack>

                      {/* Status Chips */}
                      <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        justifyContent="flex-end"
                        sx={{ gap: 0.6, flexShrink: 0 }}
                      >
                        {q._prefilledFromStep && (
                          <ReferenceBadge>
                            <HistoryRoundedIcon sx={{ fontSize: 13 }} />
                            Ref #{q._prefilledFromStep}
                          </ReferenceBadge>
                        )}

                        {excluded && statusChip("Excluded", theme.palette.warning.main, BlockRoundedIcon)}

                        {meaningful &&
                          !excluded &&
                          statusChip("Answered", theme.palette.success.main, CheckCircleRoundedIcon)}

                        {changed && statusChip("Modified", theme.palette.info.main, RuleRoundedIcon)}
                      </Stack>
                    </Stack>

                    {/* Reference Section */}
                    {hasPrev && (
                      <Paper
                        variant="outlined"
                        sx={{
                          mb: 1.75,
                          p: 1.5,
                          borderRadius: "8px",
                          bgcolor: alpha(theme.palette.info.light, 0.05),
                          borderColor: alpha(theme.palette.info.main, 0.18),
                        }}
                      >
                        <ReferenceBadge sx={{ mb: 1.25 }}>
                          <HistoryRoundedIcon sx={{ fontSize: 13 }} />
                          REFERENCE
                        </ReferenceBadge>

                        <Box
                          sx={{
                            display: "grid",
                            gap: 1.25,
                            gridTemplateColumns: {
                              xs: "1fr 1fr",
                              sm: "auto auto 1fr",
                            },
                            alignItems: "start",
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              Rating
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {prev.rating === "" || prev.rating == null ? "-" : prev.rating}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              Applicability
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {prev.applicability || "YES"}
                            </Typography>
                          </Box>
                          <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              Comment
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: prev.comments ? "text.primary" : "text.disabled",
                                whiteSpace: "pre-wrap",
                                fontStyle: prev.comments ? "normal" : "italic",
                              }}
                            >
                              {prev.comments?.trim() || "No comment provided"}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )}

                    {/* Input Fields */}
                    <Stack spacing={1.5}>
                      {!isSelfReview && (
                        <Box
                          sx={{
                            display: "grid",
                            gap: 1.5,
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          }}
                        >
                          <TextField
                            label={`Rating (${RATING_MIN}-${RATING_MAX})`}
                            required={ratingRequired}
                            type="number"
                            size={isXs ? "medium" : "small"}
                            value={q.rating}
                            onChange={(e) =>
                              onChangeQuestionField(q.questionId, "rating", e.target.value)
                            }
                            disabled={!isEditable || excluded}
                            InputProps={{
                              startAdornment: (
                                <StarRoundedIcon
                                  sx={{
                                    fontSize: 18,
                                    mr: 0.75,
                                    color: alpha(theme.palette.warning.main, 0.85),
                                  }}
                                />
                              ),
                            }}
                            inputProps={{
                              min: RATING_MIN,
                              max: RATING_MAX,
                              step: RATING_STEP,
                            }}
                            error={!!qErr.rating}
                            helperText={qErr.rating || (ratingRequired ? `Required - Step ${RATING_STEP}` : " ")}
                            sx={fieldSx(isEditable && !excluded)}
                            fullWidth
                          />

                          <TextField
                            select
                            label="Applicability"
                            size={isXs ? "medium" : "small"}
                            value={q.applicability}
                            onChange={(e) =>
                              onChangeQuestionField(q.questionId, "applicability", e.target.value)
                            }
                            disabled={!isEditable}
                            InputProps={{
                              startAdornment: (
                                <TuneRoundedIcon
                                  sx={{
                                    fontSize: 18,
                                    mr: 0.75,
                                    color: alpha(theme.palette.info.main, 0.85),
                                  }}
                                />
                              ),
                            }}
                            error={!!qErr.applicability}
                            helperText={qErr.applicability || " "}
                            sx={fieldSx(isEditable)}
                            fullWidth
                          >
                            <MenuItem value="YES">Yes - Counts toward evaluation</MenuItem>
                            <MenuItem value="NO">No - Excluded from scoring</MenuItem>
                            <MenuItem value="OPTIONAL">Optional - For reference only</MenuItem>
                          </TextField>
                        </Box>
                      )}

                      <TextField
                        label="Comments"
                        size="small"
                        multiline
                        minRows={isSelfReview ? 3 : 2}
                        value={q.comments}
                        onChange={(e) =>
                          onChangeQuestionField(q.questionId, "comments", e.target.value)
                        }
                        disabled={!isEditable}
                        InputProps={{
                          startAdornment: (
                            <ChatBubbleOutlineRoundedIcon
                              sx={{
                                fontSize: 18,
                                mr: 0.75,
                                mt: 0.25,
                                alignSelf: "flex-start",
                                color: alpha(theme.palette.primary.main, 0.7),
                              }}
                            />
                          ),
                        }}
                        error={!!qErr.comments}
                        helperText={qErr.comments || " "}
                        placeholder="Add your insights and feedback..."
                        sx={fieldSx(isEditable)}
                        fullWidth
                      />
                    </Stack>

                    {/* Action Button */}
                    {isEditable && (
                      <Box sx={{ mt: 1.5, textAlign: "right" }}>
                        <Tooltip title="Scroll to and focus this question">
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<CenterFocusStrongRoundedIcon fontSize="small" />}
                            onClick={() => onFocusQuestion(q.questionId)}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: theme.palette.text.secondary,
                              "&:hover": { color: theme.palette.primary.main },
                            }}
                          >
                            Focus this question
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </QuestionCard>
              );
            })}
          </Stack>
        )}

        {/* ===== Action Bar (sticky, blurred) ===== */}
        <Box sx={{ mt: 2, position: "sticky", bottom: { xs: 10, md: 16 }, zIndex: 10 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 1.75 },
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
              background: `linear-gradient(145deg,
                ${alpha(theme.palette.background.paper, 0.95)} 0%,
                ${alpha(theme.palette.background.default, 0.85)} 100%)`,
              backdropFilter: "blur(12px)",
              boxShadow: `0 12px 34px ${alpha(theme.palette.common.black, 0.12)}`,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                height: 3,
                background: `linear-gradient(90deg,
                  ${alpha(theme.palette.primary.main, 0)} 0%,
                  ${alpha(theme.palette.primary.main, 0.4)} 30%,
                  ${alpha(secondaryMain, 0.4)} 70%,
                  ${alpha(secondaryMain, 0)} 100%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`${isSelfReview ? "Self Review" : "Evaluation"} - ${
                      isEditable ? "Editable" : "View only"
                    }`}
                    sx={{
                      height: 24,
                      fontWeight: 800,
                      fontSize: "0.7rem",
                      borderRadius: "8px",
                      backgroundColor: alpha(
                        isEditable ? theme.palette.info.main : theme.palette.text.primary,
                        isEditable ? 0.1 : 0.06
                      ),
                      color: isEditable ? theme.palette.info.main : theme.palette.text.secondary,
                      border: `1px solid ${alpha(
                        isEditable ? theme.palette.info.main : theme.palette.divider,
                        0.2
                      )}`,
                    }}
                  />
                  {!isSelfReview && overallAutoValue && (
                    <Chip
                      size="small"
                      icon={<StarRoundedIcon sx={{ fontSize: 15 }} />}
                      label={`Overall ${overallAutoValue}`}
                      sx={{
                        height: 24,
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        borderRadius: "8px",
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        "& .MuiChip-icon": { color: theme.palette.primary.main },
                      }}
                    />
                  )}
                </Stack>

                {isEditable && requirements.totalMissing > 0 && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{
                      mt: 0.75,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <RuleRoundedIcon sx={{ fontSize: 14 }} />
                    {requirements.totalMissing} required field{requirements.totalMissing > 1 ? "s" : ""} remaining
                  </Typography>
                )}
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={onBack}
                  disabled={submitting}
                  fullWidth={isXs}
                  sx={{
                    borderRadius: "8px",
                    px: 3,
                    textTransform: "none",
                    fontWeight: 700,
                    borderColor: alpha(theme.palette.divider, 0.32),
                    "&:hover": {
                      borderColor: alpha(theme.palette.divider, 0.5),
                      backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    },
                  }}
                >
                  Cancel
                </Button>

                {isEditable && (
                  <Button
                    variant="contained"
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={onSubmit}
                    disabled={submitDisabled}
                    fullWidth={isXs}
                    sx={{
                      borderRadius: "8px",
                      px: 4,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${secondaryMain})`,
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.24)}`,
                      transition: "all 180ms ease",
                      "&:hover": {
                        boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.32)}`,
                        filter: "brightness(1.03)",
                        transform: "translateY(-1px)",
                      },
                      "&.Mui-disabled": {
                        background: alpha(theme.palette.primary.main, 0.3),
                        color: alpha(theme.palette.common.white, 0.7),
                      },
                    }}
                  >
                    {submitting ? "Submitting..." : "Submit Evaluation"}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Box>
      </CardContent>
    </GlassCard>
  );
}
